import * as React from "react";
import { type Components } from "react-markdown";
import { CitationButton } from "@/components/feature/layout/chat/citationButton";
import { type Evidence } from "@/types/evidences";
import type { Artefact } from "@/modules/chat/chat-ui.types";
import { isLetterArtefact } from "@/modules/chat/chat-ui.types";
import { InlineArtefactViewer } from "@/components/feature/artefactsViewer/InlineArtefactViewer";
import { LetterArtefactViewer } from "@/components/feature/artefactsViewer/LetterArtefactViewer";
import { WebCitationButton } from "./WebCitationButton";
import { CopyableTable } from "./CopyableTable";
import type { WebSearchSource } from "@/types/open-chat";
import { SnapshotCitationButton } from "@/modules/snapshots/outcome-details/components/SnapshotCitationButton";
import type { PlacedSnapshotCitation } from "@/modules/snapshots/businessReports.types";

const NUMERIC_CITATION_REGEX = /\[(?:Id:)?(\d+(?:,\s*(?:Id:)?\d+)*)\](?!\()/g;
const UUID_CITATION_REGEX = /\[([\w\-\s,]+)\]/g;
const ULID_PATTERN =
  /^([0-9A-HJKMNP-TV-Z]{26}|[0-9A-HJKMNP-TV-Z]{26}-[0-9A-HJKMNP-TV-Z]{26}-[0-9A-HJKMNP-TV-Z]{26}-\d+-\d+)$/;

interface RenderersContext {
  evidences: Evidence[];
  onCitationClick?: () => void;
  onArtefactClick?: (artefactId: string) => void;
  inlineArtefacts?: Artefact[];
  webSearchSources?: WebSearchSource[];
  onWebCitationClick?: (sourceIds: number[]) => void;
  isStreaming?: boolean;
  snapshotCitations?: PlacedSnapshotCitation[];
  onSnapshotCitationClick?: () => void;
}

const createCitationButton = (
  evidence: Evidence | undefined,
  citationId: string | number,
  matchPosition: number,
  idIndex: number,
  onCitationClick?: () => void,
): React.ReactNode => {
  if (!evidence) {
    return null;
  }

  const stableKey = `citation-${citationId}-pos${matchPosition}-idx${idIndex}`;

  return (
    <CitationButton
      key={stableKey}
      citation={typeof citationId === "number" ? citationId : citationId.toString()}
      highlights={evidence.highlights}
      fseId={evidence.fseId}
      documentId={evidence.documentId}
      onCitationClick={onCitationClick}
      index={idIndex}
      spreadsheetTarget={evidence.spreadsheetTarget}
      sourceDocumentContentType={evidence.sourceDocumentContentType}
    />
  );
};

const wrapCitationButtons = (buttons: React.ReactNode[], matchPosition: number): React.ReactNode => {
  if (!buttons.length) return null;
  return (
    <span key={`citation-group-pos${matchPosition}`} className="inline-flex flex-wrap items-center gap-1">
      {buttons}
    </span>
  );
};

/**
 * Relocate citations that the LLM placed mid-word to after the word boundary.
 * E.g. "voc[1]ation" -> "vocation[1]", "T[2]hese" -> "These[2]"
 */
const relocateMidWordCitations = (text: string): string =>
  text.replace(/(\w)((?:\[(?:Id:)?\d+(?:,\s*(?:Id:)?\d+)*\](?!\())+)(\w+)/g, "$1$3$2");

const processCitationsInText = (text: string, context: RenderersContext): React.ReactNode[] => {
  const processed = relocateMidWordCitations(text);
  const result: React.ReactNode[] = [];
  let lastProcessedIndex = 0;

  type MatchInfo = {
    index: number;
    length: number;
    type: "numeric" | "uuid";
    content: string;
    ids: (string | number)[];
  };

  const matches: MatchInfo[] = [];

  NUMERIC_CITATION_REGEX.lastIndex = 0;
  let match;
  while ((match = NUMERIC_CITATION_REGEX.exec(processed)) !== null) {
    const ids = match[1]
      .split(/,\s*/)
      .map(id => id.replace("Id:", ""))
      .map(Number)
      .filter(id => !isNaN(id));
    matches.push({
      index: match.index,
      length: match[0].length,
      type: "numeric",
      content: match[0],
      ids,
    });
  }

  UUID_CITATION_REGEX.lastIndex = 0;
  while ((match = UUID_CITATION_REGEX.exec(processed)) !== null) {
    const overlapsNumeric = matches.some(m => match!.index >= m.index && match!.index < m.index + m.length);
    if (overlapsNumeric) continue;

    const looksLikeMarkdownLink = processed[match.index + match[0].length] === "(";
    if (looksLikeMarkdownLink) continue;

    const ids = match[1]
      .split(/[, ]+/)
      .map(id => id.trim())
      .filter(id => id.length > 0);

    matches.push({
      index: match.index,
      length: match[0].length,
      type: "uuid",
      content: match[0],
      ids,
    });
  }

  matches.sort((a, b) => a.index - b.index);

  // Merge consecutive numeric matches: [1][2][3] -> single group with ids [1,2,3]
  const mergedMatches: MatchInfo[] = [];
  for (const matchInfo of matches) {
    const prev = mergedMatches[mergedMatches.length - 1];
    if (
      prev &&
      prev.type === "numeric" &&
      matchInfo.type === "numeric" &&
      matchInfo.index === prev.index + prev.length
    ) {
      prev.ids.push(...matchInfo.ids);
      prev.length += matchInfo.length;
      prev.content += matchInfo.content;
    } else {
      mergedMatches.push({ ...matchInfo, ids: [...matchInfo.ids] });
    }
  }

  for (const matchInfo of mergedMatches) {
    if (matchInfo.index > lastProcessedIndex) {
      result.push(processed.slice(lastProcessedIndex, matchInfo.index));
    }

    if (matchInfo.type === "numeric") {
      const buttons = matchInfo.ids
        .map((id, idIndex) => {
          const evidence = context.evidences.find(ev => parseInt(ev.citationId, 10) === id);
          return createCitationButton(evidence, id, matchInfo.index, idIndex, context.onCitationClick);
        })
        .filter(Boolean);

      if (buttons.length > 0) {
        const wrapped = wrapCitationButtons(buttons, matchInfo.index);
        if (wrapped) result.push(wrapped);
      } else if (context.webSearchSources?.length) {
        const matchedSources = matchInfo.ids
          .map(id => context.webSearchSources!.find(s => s.id === id))
          .filter((s): s is WebSearchSource => s != null);
        if (matchedSources.length > 0) {
          result.push(
            <WebCitationButton
              key={`web-group-pos${matchInfo.index}`}
              sources={matchedSources}
              onClick={context.onWebCitationClick}
            />,
          );
        }
      }
    } else if (matchInfo.type === "uuid") {
      const buttons = matchInfo.ids
        .map((id, idIndex) => {
          const strId = String(id);
          const evidence = context.evidences.find(ev => ev.id.replace("evidence-", "") === strId);

          if (evidence) {
            return createCitationButton(evidence, strId, matchInfo.index, idIndex, context.onCitationClick);
          }

          // Snapshot citation fallback: ULID-shaped ID matching a row in snapshotCitations
          if (ULID_PATTERN.test(strId)) {
            const snapshotCite = context.snapshotCitations?.find(c => c.snapshot_outcome_result_id === strId);
            if (snapshotCite) {
              return (
                <SnapshotCitationButton
                  key={`snap-cite-${strId}-pos${matchInfo.index}-idx${idIndex}`}
                  outcomeResultId={snapshotCite.snapshot_outcome_result_id}
                  snapshotOutcomeId={snapshotCite.snapshot_outcome_id}
                  startOffset={snapshotCite.start_offset}
                  endOffset={snapshotCite.end_offset}
                  onCitationClick={context.onSnapshotCitationClick}
                />
              );
            }
            return null;
          }
          return null;
        })
        .filter(Boolean);

      const wrapped = wrapCitationButtons(buttons, matchInfo.index);
      if (wrapped) result.push(wrapped);
    }

    lastProcessedIndex = matchInfo.index + matchInfo.length;
  }

  if (lastProcessedIndex < processed.length) {
    result.push(processed.slice(lastProcessedIndex));
  }

  return result.length > 0 ? result : [processed];
};

const processChildren = (children: React.ReactNode, context: RenderersContext): React.ReactNode => {
  if (typeof children === "string") {
    const processed = processCitationsInText(children, context);
    return processed.length === 1 ? processed[0] : <>{processed}</>;
  }

  if (Array.isArray(children)) {
    return children.map((child, idx) => <React.Fragment key={idx}>{processChildren(child, context)}</React.Fragment>);
  }

  if (React.isValidElement(children)) {
    const childProps = children.props as { children?: React.ReactNode };
    if (childProps.children) {
      return React.cloneElement(children, {}, processChildren(childProps.children, context));
    }
  }

  return children;
};

export const createCustomRenderers = (context: RenderersContext): Partial<Components> => ({
  p: ({ children }) => <p className="mb-2 whitespace-pre-wrap">{processChildren(children, context)}</p>,
  li: ({ children }) => <li className="pl-2">{processChildren(children, context)}</li>,
  td: ({ children }) => <td className="px-4 py-3 text-foreground">{processChildren(children, context)}</td>,
  h1: ({ children }) => (
    <h1 className="mb-4 mt-6 whitespace-pre-wrap text-2xl font-bold">{processChildren(children, context)}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-3 mt-5 whitespace-pre-wrap text-xl font-bold">{processChildren(children, context)}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-2 mt-4 whitespace-pre-wrap text-lg font-bold">{processChildren(children, context)}</h3>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-4 whitespace-pre-wrap border-l-4 border-gray-300 pl-4 italic">
      {processChildren(children, context)}
    </blockquote>
  ),
  a: ({ children, href }) => {
    if (href?.startsWith("artefact://")) {
      const artefactId = href.replace("artefact://", "");

      if (context.inlineArtefacts) {
        const artefact = context.inlineArtefacts.find(a => a.id === artefactId);
        if (artefact) {
          if (isLetterArtefact(artefact)) {
            return <LetterArtefactViewer artefact={artefact} onExpand={context.onArtefactClick} />;
          }
          return <InlineArtefactViewer artefact={artefact} onExpand={context.onArtefactClick} />;
        }
      }

      if (context.onArtefactClick) {
        return (
          <button
            type="button"
            onClick={() => context.onArtefactClick!(artefactId)}
            className="my-3 inline-flex items-center gap-1 rounded-md border border-brand-gold-200 bg-brand-gold-50 px-2 py-2 text-sm font-medium text-brand-gold-600 transition-colors hover:bg-brand-gold-100 hover:text-brand-gold-700"
          >
            {children}
          </button>
        );
      }
    }

    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-brand-gold-600 underline">
        {children}
      </a>
    );
  },
  strong: ({ children }) => <strong className="whitespace-pre-wrap font-semibold">{children}</strong>,
  em: ({ children }) => <em className="whitespace-pre-wrap italic">{children}</em>,
  code: ({
    className,
    children,
    ...props
  }: React.ClassAttributes<HTMLElement> & React.HTMLAttributes<HTMLElement> & { inline?: boolean }) => {
    const match = /language-(\w+)/.exec(className || "");
    const inline = props.inline;
    return !inline && match ? (
      <pre className="my-4 whitespace-pre-wrap rounded p-2">
        <code className={className} {...props}>
          {children}
        </code>
      </pre>
    ) : (
      <code className="whitespace-pre-wrap rounded px-1" {...props}>
        {children}
      </code>
    );
  },
  ul: ({ children }) => <ul className="my-4 list-[circle] space-y-2 pl-6">{children}</ul>,
  ol: ({ children }) => <ol className="my-4 list-decimal space-y-2 pl-6">{children}</ol>,
  table: ({ children }) => <CopyableTable hideActions={context.isStreaming}>{children}</CopyableTable>,
  thead: ({ children }) => <thead className="border-b border-desert-sand-300 bg-ivory-mist-100">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-desert-sand-200">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-ivory-mist-50 transition-colors">{children}</tr>,
  th: ({ children }) => (
    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground">{children}</th>
  ),
});

/**
 * Basic renderers without citation processing (for backward compatibility)
 */
export const customRenderers: Partial<Components> = createCustomRenderers({ evidences: [] });
